import { PropsWithChildren } from "react"
import { MAX_EMAIL_LENGTH } from "../../../../../../../constants/inputValidationDefaults"
import { Box, FormLabel, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { FormikDropdown, FormikTextField } from "../../../../../../../shared/components/forms/Formik"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { TranslationKey } from "../../../../../../localization/translations"

import { CompanyUserFormValues, UserLanguageOption } from "../../companyUserFormTypes"

type SelectOption = Readonly<{
    id: number
    name: string
}>

type UserDetailsTabProps = Readonly<{
    userGroups: ReadonlyArray<SelectOption>
    departments: ReadonlyArray<SelectOption>
    languageOptions: ReadonlyArray<UserLanguageOption>
}>

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
    },
    field: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
    },
    label: {
        "&.MuiFormLabel-root": {
            color: theme.palette.text.primary,
            fontWeight: 600,
        },
        "&.Mui-focused": {
            color: theme.palette.text.primary,
        },
        "& .MuiFormLabel-asterisk": {
            color: theme.palette.error.main,
        },
    },
    narrow: {
        maxWidth: "24rem",
    },
    narrowLanguage: {
        maxWidth: "18rem",
    },
    placeholderOption: {
        color: theme.palette.text.secondary,
    },
}))

type FieldRowProps = Readonly<
    PropsWithChildren<{
        labelId: TranslationKey
        label: string
        required?: boolean
        className?: string
    }>
>

function FieldRow({ labelId, label, required, className, children }: FieldRowProps) {
    const { classes, cx } = useStyles()

    return (
        <Box className={cx(classes.field, className)}>
            <LocalizedStrict id={labelId}>
                <FormLabel className={classes.label} required={required}>
                    {label}
                </FormLabel>
            </LocalizedStrict>
            {children}
        </Box>
    )
}

export function UserDetailsTab({ userGroups, departments, languageOptions }: UserDetailsTabProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Box className={classes.root}>
            <FieldRow labelId="company-user-field-user-group" label="User group">
                <FormikDropdown<CompanyUserFormValues> name="userGroupId" fullWidth displayEmpty>
                    <MenuItem value="">
                        <LocalizedStrict id="company-user-field-user-group-placeholder">
                            <span className={classes.placeholderOption}>Select user group...</span>
                        </LocalizedStrict>
                    </MenuItem>
                    {userGroups.map((group) => (
                        <MenuItem key={group.id} value={String(group.id)}>
                            {group.name}
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </FieldRow>

            <FieldRow labelId="company-user-field-department" label="Department">
                <FormikDropdown<CompanyUserFormValues> name="departmentId" fullWidth displayEmpty>
                    <MenuItem value="">
                        <LocalizedStrict id="company-user-field-department-placeholder">
                            <span className={classes.placeholderOption}>Select department...</span>
                        </LocalizedStrict>
                    </MenuItem>
                    {departments.map((department) => (
                        <MenuItem key={department.id} value={String(department.id)}>
                            {department.name}
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </FieldRow>

            <FieldRow labelId="company-user-field-employee-id" label="Employee ID">
                <FormikTextField<CompanyUserFormValues>
                    name="employeeId"
                    placeholder={l10n.getString("company-user-field-employee-id-placeholder")}
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-name" label="Name" required>
                <FormikTextField<CompanyUserFormValues>
                    name="displayName"
                    placeholder={l10n.getString("company-user-field-name-placeholder")}
                    autoComplete="name"
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-initials" label="Initials" className={classes.narrow}>
                <FormikTextField<CompanyUserFormValues>
                    name="initials"
                    placeholder={l10n.getString("company-user-field-initials-placeholder")}
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-username" label="Username">
                <FormikTextField<CompanyUserFormValues>
                    name="username"
                    placeholder={l10n.getString("company-user-field-username-placeholder")}
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-email" label="Email" required>
                <FormikTextField<CompanyUserFormValues>
                    name="email"
                    maxLength={MAX_EMAIL_LENGTH}
                    placeholder={l10n.getString("company-user-field-email-placeholder")}
                    autoComplete="email"
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-mobile" label="Mobile" className={classes.narrow}>
                <FormikTextField<CompanyUserFormValues>
                    name="mobileNumber"
                    placeholder={l10n.getString("company-user-field-mobile-placeholder")}
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-phone" label="Phone no." className={classes.narrow}>
                <FormikTextField<CompanyUserFormValues>
                    name="phoneNumber"
                    placeholder={l10n.getString("company-user-field-phone-placeholder")}
                    fullWidth
                />
            </FieldRow>

            <FieldRow labelId="company-user-field-language" label="Language" className={classes.narrowLanguage}>
                <FormikDropdown<CompanyUserFormValues> name="language" fullWidth displayEmpty>
                    <MenuItem value="">
                        <LocalizedStrict id="company-user-field-select-placeholder">
                            <span className={classes.placeholderOption}>Not selected</span>
                        </LocalizedStrict>
                    </MenuItem>
                    {languageOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {l10n.getString(option.labelKey)}
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </FieldRow>
        </Box>
    )
}
